import React, { useCallback } from 'react';
import { ariaDescribedByIds, GenericObjectType, WidgetProps } from '@rjsf/utils';
import isNil from 'lodash/isNil';

import type { SetStateAction } from 'react';

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';

interface ISelectWidget<T, S, F> extends Omit<WidgetProps<T, S, F>, 'value'> {
	value: string;
}

import { Wrapper } from '../shared';

const Widget = <T, S, F>({
	disabled,
	formContext = {} as F,
	id,
	onChange,
	options,
	placeholder,
	readonly,
	schema,
	value = '',
	name,
}: ISelectWidget<T, S, F>) => {
	const { readonlyAsDisabled = true } = formContext as GenericObjectType;

	const enumOptions = options?.enumOptions ?? [];

	const handleChange = useCallback((nextValue: SetStateAction<string>) => {
		if (nextValue) {
			if (schema?.type === "number" || schema?.type === "integer") {
				onChange(nextValue === null ? schema.default : +nextValue || schema.default);

				return;
			}

			onChange(nextValue === null ? schema.default : nextValue || schema.default);
		}
	}, [schema]);

	return (
		<Select
			onValueChange={!readonly ? handleChange : undefined}
			disabled={disabled || (readonlyAsDisabled && readonly)}
			aria-describedby={ariaDescribedByIds<T>(id)}
			value={!isNil(value) ? value : undefined}
			name={name}
		>
			<SelectTrigger className="w-full">
				<SelectValue placeholder={placeholder} />
			</SelectTrigger>

			<SelectContent>
				<SelectGroup>
					{enumOptions.map(({ value, label }) => (
						<SelectItem key={`option-${value}`} value={value}>
							{label}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}

const SelectWidget = (props) => {
	const { value, onChange } = props;

	return (
		<Wrapper value={value} onChange={onChange}>
			<Widget {...props} />
		</Wrapper>
	)
}

export { SelectWidget };
